import React, {LegacyRef, useRef} from 'react';
import {useNavigate} from 'react-router';
import {getMyCars} from '@di-shared/api/cci/auth';
import {InteractionLottie} from '@di-shared/features/about/About/index';
import {emitEventLog} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import usePendingHandler from '@shared/usePendingHandler';

const HEADER_HEIGHT = 60;

export default function () {
  const {t} = useInferableTranslation();
  const {backOrClose} = useCcWebviewUtilsHelper();
  const startPointRef = useRef<Element>(null);
  const {vin, setVin} = useEnvironmentStore(({vin, setVin}) => ({vin, setVin}));
  const navigate = useNavigate();

  const [goToHomePage] = usePendingHandler(
    async () => {
      let targetVin = vin;
      if (!targetVin) {
        const data = await getMyCars();
        const mainVehicleVin = data.contents.find(({isMainVehicle}) => isMainVehicle)?.vin;
        if (!mainVehicleVin) {
          backOrClose();
          return;
        }
        setVin(mainVehicleVin);
        targetVin = mainVehicleVin;
      }
      navigate(ROUTE_PATHS.HOME.getFullPath(targetVin), {replace: true});
    },
    () => {
      backOrClose();
    }
  );

  function scrollToStart() {
    if (startPointRef.current) {
      window.scrollTo({
        top: startPointRef.current.getBoundingClientRect().y - HEADER_HEIGHT,
        behavior: 'smooth',
      });
    }
  }

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T40132')}</span>
        </h1>
      </Header>

      <main className="content page_home">
        <section className="section section_intro">
          <section className="section intro_driving_insight">
            <div className="intro_heading">
              <span
                className="ic logo_driving_insight"
                aria-hidden="true"
              />
              <h2
                className="intro_heading_title"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T40133')}
              </h2>
            </div>
            <div className="intro_motion">
              <InteractionLottie
                id="intro_driving_insight_light.json"
                className="intro_img"
                kiaLightSrc={() => import('@di-shared/assets/img/json/intro_driving_insight_light.json')}
                kiaDarkSrc={() => import('@di-shared/assets/img/json/intro_driving_insight.json')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/json/hyundai/intro_driving_insight_light.json')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/json/hyundai/intro_driving_insight.json')}
                genesisLightSrc={() => import('@di-shared/assets/img/json/genesis/intro_driving_insight_light.json')}
                genesisDarkSrc={() => import('@di-shared/assets/img/json/genesis/intro_driving_insight.json')}
                loop={false}
                autoplay
              />
            </div>
            <button
              type="button"
              className="intro_start_btn"
              onClick={() => scrollToStart()}
              aria-hidden="true"
            >
              {t('T40132')}
              <span className="ic ic_arrow_down_16px" />
            </button>
          </section>
          <section
            ref={startPointRef as LegacyRef<HTMLDivElement>}
            className="section intro_my_trips"
          >
            <div className="intro_heading">
              <h3 className="intro_title">{t('T40134')}</h3>
              <p
                className="intro_desc"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T40135')}
              </p>
            </div>
            <div className="intro_motion">
              <InteractionLottie
                id="intro_my_trips_light.json"
                className="intro_img"
                kiaLightSrc={() => import('@di-shared/assets/img/json/intro_my_trips_light.json')}
                kiaDarkSrc={() => import('@di-shared/assets/img/json/intro_my_trips.json')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/json/hyundai/intro_my_trips_light.json')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/json/hyundai/intro_my_trips.json')}
                genesisLightSrc={() => import('@di-shared/assets/img/json/genesis/intro_my_trips_light.json')}
                genesisDarkSrc={() => import('@di-shared/assets/img/json/genesis/intro_my_trips.json')}
                loop={false}
                autoplay={false}
              />
            </div>
          </section>
          <section className="section intro_car_management">
            <div className="intro_heading">
              <h3 className="intro_title">{t('T40136')}</h3>
              <p
                className="intro_desc"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T40137')}
              </p>
            </div>
            <div className="intro_motion">
              <InteractionLottie
                id="intro_car_management_light.json"
                className="intro_img"
                kiaLightSrc={() => import('@di-shared/assets/img/json/intro_car_management_light.json')}
                kiaDarkSrc={() => import('@di-shared/assets/img/json/intro_car_management.json')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/json/hyundai/intro_car_management_light.json')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/json/hyundai/intro_car_management.json')}
                genesisLightSrc={() => import('@di-shared/assets/img/json/genesis/intro_car_management_light.json')}
                genesisDarkSrc={() => import('@di-shared/assets/img/json/genesis/intro_car_management.json')}
                loop={false}
                autoplay={false}
              />
            </div>
          </section>
          <section className="section intro_benefits">
            <div className="intro_heading">
              <h3 className="intro_title">{t('T40138')}</h3>
              <p
                className="intro_desc"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T40139')}
              </p>
            </div>
            <div className="intro_motion">
              <InteractionLottie
                id="intro_benefits_light.json"
                className="intro_img"
                kiaLightSrc={() => import('@di-shared/assets/img/json/intro_benefits_light.json')}
                kiaDarkSrc={() => import('@di-shared/assets/img/json/intro_benefits.json')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/json/hyundai/intro_benefits_light.json')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/json/hyundai/intro_benefits.json')}
                genesisLightSrc={() => import('@di-shared/assets/img/json/genesis/intro_benefits_light.json')}
                genesisDarkSrc={() => import('@di-shared/assets/img/json/genesis/intro_benefits.json')}
                loop={false}
                autoplay={false}
              />
            </div>
          </section>
          <section className="section intro_data_explore">
            <div className="section_container">
              <div
                className="section_container_header"
                aria-hidden="true"
              >
                <h3 className="title_xsmall_b">{t('T20094')}</h3>
                <span className="ic ic_arrow_forward_16px" />
              </div>
              <p
                className="section_container_desc"
                aria-hidden="true"
              >
                {t('T40140')}
              </p>
              <div className="intro_motion">
                <InteractionLottie
                  id="intro_data_explore_light.json"
                  className="intro_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/json/intro_data_explore_light.json')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/json/intro_data_explore.json')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/json/hyundai/intro_data_explore_light.json')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/json/hyundai/intro_data_explore.json')}
                  genesisLightSrc={() => import('@di-shared/assets/img/json/genesis/intro_data_explore_light.json')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/json/genesis/intro_data_explore.json')}
                  loop={false}
                  autoplay={false}
                />
              </div>
              <LinkTo
                to={ROUTE_PATHS.ABOUT_DATA_EXPLORE.getFullPath()}
                className="section_container_full_btn"
                aria-label={`${t('T20094')} ${t('T40140')}}`}
              />
            </div>
          </section>
        </section>
        <div className="divider divider_thick" />
        <section className="section section_notice">
          <ul className="notice_list">
            <li className="notice_item">{t('T40141')}</li>
            <li className="notice_item">{t('T40142')}</li>
          </ul>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          tabIndex={1}
          onClick={() => {
            emitEventLog({
              event: 'di_driveinfo_interaction',
              data_action: 'start driving insight',
              data_name: t('T40143'),
            });
            goToHomePage();
          }}
        >
          {t('T40143')}
        </button>
      </div>
    </div>
  );
}
